import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { notifyMissedCall } from "@/lib/notifications";

interface ActiveCall {
  id: string;
  caller_id: string;
  receiver_id: string;
  room_name: string;
  room_url: string;
  status: string;
  created_at: string;
  caller_profile?: {
    name: string;
    avatar_url: string | null;
  };
}

export const useVideoCall = () => {
  const { user } = useAuth();
  const [activeCall, setActiveCall] = useState<ActiveCall | null>(null);
  const [incomingCall, setIncomingCall] = useState<ActiveCall | null>(null);
  const [isInCall, setIsInCall] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Listen for incoming calls
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel('incoming-calls')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'active_calls',
          filter: `receiver_id=eq.${user.id}`,
        },
        async (payload) => {
          const call = payload.new as ActiveCall;
          if (call.status === 'ringing') {
            // Fetch caller profile
            const { data: callerProfile } = await supabase
              .from('profiles')
              .select('name, avatar_url')
              .eq('id', call.caller_id)
              .single();

            setIncomingCall({
              ...call,
              caller_profile: callerProfile || undefined,
            });
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'active_calls',
        },
        (payload) => {
          const call = payload.new as ActiveCall;
          
          // Handle call status changes
          if (call.status === 'ended' || call.status === 'declined') {
            if (activeCall?.id === call.id) {
              setActiveCall(null);
              setIsInCall(false);
            }
            if (incomingCall?.id === call.id) {
              setIncomingCall(null);
            }
          }
          
          if (call.status === 'active' && call.caller_id === user.id) {
            // Receiver accepted the call
            setActiveCall(call);
            setIsInCall(true);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, activeCall?.id, incomingCall?.id]);

  const startCall = useCallback(async (receiverId: string) => {
    if (!user) {
      toast.error("You must be logged in to make calls");
      return null;
    }

    setIsLoading(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) {
        throw new Error("No session");
      }

      const response = await supabase.functions.invoke('create-daily-room', {
        body: { receiverId },
      });

      if (response.error) {
        throw new Error(response.error.message);
      }

      const { callId, roomUrl, roomName } = response.data;

      // Fetch receiver profile
      const { data: receiverProfile } = await supabase
        .from('profiles')
        .select('name, avatar_url')
        .eq('id', receiverId)
        .single();

      const call: ActiveCall = {
        id: callId,
        caller_id: user.id,
        receiver_id: receiverId,
        room_name: roomName,
        room_url: roomUrl,
        status: 'ringing',
        created_at: new Date().toISOString(),
        caller_profile: receiverProfile || undefined,
      };

      setActiveCall(call);
      setIsInCall(true);
      return call;
    } catch (error) {
      console.error('Error starting call:', error);
      toast.error("Failed to start video call");
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  const acceptCall = useCallback(async () => {
    if (!incomingCall || !user) return;

    try {
      await supabase
        .from('active_calls')
        .update({ 
          status: 'active',
          started_at: new Date().toISOString(),
        })
        .eq('id', incomingCall.id);

      setActiveCall(incomingCall);
      setIncomingCall(null);
      setIsInCall(true);
    } catch (error) {
      console.error('Error accepting call:', error);
      toast.error("Failed to accept call");
    }
  }, [incomingCall, user]);

  const declineCall = useCallback(async (reason: 'declined' | 'missed' = 'declined') => {
    if (!incomingCall || !user) return;

    try {
      await supabase
        .from('active_calls')
        .update({ 
          status: reason,
          ended_at: new Date().toISOString(),
        })
        .eq('id', incomingCall.id);

      // Get current user's name to notify the caller
      const { data: currentUserProfile } = await supabase
        .from('profiles')
        .select('name')
        .eq('id', user.id)
        .single();

      // Send missed call notification to the caller
      await notifyMissedCall(
        incomingCall.caller_id,
        currentUserProfile?.name || 'Someone',
        reason
      );

      setIncomingCall(null);
    } catch (error) {
      console.error('Error declining call:', error);
    }
  }, [incomingCall, user]);

  const endCall = useCallback(async () => {
    if (!activeCall) return;

    try {
      await supabase
        .from('active_calls')
        .update({ 
          status: 'ended',
          ended_at: new Date().toISOString(),
        })
        .eq('id', activeCall.id);

      setActiveCall(null);
      setIsInCall(false);
    } catch (error) {
      console.error('Error ending call:', error);
    }
  }, [activeCall]);

  return {
    activeCall,
    incomingCall,
    isInCall,
    isLoading,
    startCall,
    acceptCall,
    declineCall,
    endCall,
  };
};
